import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, Directive, Input, NgModule } from '@angular/core';

var SeFeScrollToAlignment;
(function (SeFeScrollToAlignment) {
    SeFeScrollToAlignment["Center"] = "center";
    SeFeScrollToAlignment["Top"] = "top";
})(SeFeScrollToAlignment || (SeFeScrollToAlignment = {}));

class SeFeScrollService {
    constructor() {
        this.MAX_FPS = 60; // frames per second - used to calculate interval duration
        this.DEFAULT_DURATION = 400; // ms - total duration of animation
        this.DEFAULT_OFFSET = 24; // px - minimum distance from the top when content is longer than the viewport
        this.MIN_VISIBLE_CONTENT = 100; // px - ensures at least this much content is visible at max scroll
        this.commands = [];
    }
    get window() { return window; }
    get document() { return document; }
    // PUBLIC METHODS
    // When scrolling to a specific element we first make sure to cancel any
    // other animations inside the same scroll parent to avoid odd behavior.
    scrollTo(align, target, options = {}) {
        const container = this.scrollParent(target);
        const cancel = () => this.cancelCommand(command, interval);
        const interval = setInterval(() => this.processCommand(command), 1000 / this.MAX_FPS);
        const command = { target, container, align, cancel, options };
        const exisingCommand = this.commands.find(c => c.container === container);
        if (exisingCommand)
            exisingCommand.cancel();
        this.updateFocus(target);
        this.commands.push(command);
    }
    // Public scrollParent method that also works across (open) shadow dom
    // and will return the first element with css scrolling or the window
    scrollParent(el) {
        let parent = this.parentElement(el);
        while (parent) {
            const style = this.window.getComputedStyle(parent);
            const overflow = `${style.overflow}${style.overflowY}`;
            if (overflow.match(/scroll|auto/))
                return parent;
            el = parent;
            parent = this.parentElement(el);
        }
        return this.window;
    }
    // Stop a container from scrolling all the way to the bottom to prevent the browser
    // from automatically changing the scroll position when the content size changes
    preventAutoScroll(el) {
        if (el.seAutoScrollPrevented)
            return;
        el.seAutoScrollPrevented = true;
        el.addEventListener('scroll', () => {
            const top = this.scrollTop(el);
            const height = this.height(el);
            const bottom = Math.ceil(top + height);
            const scrollHeight = this.scrollHeight(el);
            const maxTop = scrollHeight - height - 1;
            if (bottom >= scrollHeight)
                this.scrollTopTo(el, maxTop);
        });
    }
    // PRIVATE METHODS
    // Commands are processed on an interval until the complete or are canceled
    processCommand(command) {
        if (!command.startTime) {
            command.startTime = Date.now();
            // Exit the first loop early to make sure the content is rendered before we scroll.
            // Our directive sets this to false since it's already running after a template render.
            if (command.options.waitForRender !== false)
                return;
        }
        command.initialOffset || (command.initialOffset = this.initialOffset(command));
        this.preventAutoScroll(command.container);
        this.setScrollBuffer(command);
        this.setScrollPosition(command);
    }
    cancelCommand(command, interval) {
        clearInterval(interval);
        const commandIndex = this.commands.indexOf(command);
        if (commandIndex !== -1)
            this.commands.splice(commandIndex, 1);
    }
    setScrollPosition(command) {
        const delta = this.calcDelta(command);
        const position = this.calcPosition(command, delta);
        const el = command.container;
        this.scrollTopTo(el, position.top);
        if (delta === 1)
            command.cancel();
    }
    scrollTopTo(el, top) {
        if (this.isWindow(el)) {
            el.scrollTo(el.scrollX, top);
        }
        else {
            el.scrollTop = top;
        }
    }
    scrollHeight(el) {
        if (this.isWindow(el)) {
            const b = this.document.body;
            const d = this.document.documentElement;
            return Math.max(b.scrollHeight, b.offsetHeight, d.clientHeight, d.scrollHeight, d.offsetHeight);
        }
        else {
            return el.scrollHeight;
        }
    }
    setScrollBuffer(command) {
        if (!command.options.allowOverflow)
            return;
        this.setStyle(command.container, 'padding-bottom', `${this.calcBuffer(command).vert}px`);
    }
    setStyle(el, prop, value) {
        this.containerEl(el).style.setProperty(prop, value);
    }
    parentElement(el) {
        var _a;
        return el.parentElement || ((_a = el.parentNode) === null || _a === void 0 ? void 0 : _a.host); // parentNode.host walks through shadow roots
    }
    calcDelta(command) {
        const elapsed = Date.now() - command.startTime;
        let duration = command.options.duration;
        if (isNaN(duration))
            duration = this.DEFAULT_DURATION;
        if (duration === 0)
            return 1;
        return this.easeOutBack(Math.min(elapsed / duration, 1));
    }
    calcPosition(command, delta) {
        const offset = this.calcOffset(command); // visual offset inside container scroll area
        const position = this.positionInContainer(command); // actual offset from container top
        const top = position.top - (offset.top + (command.initialOffset.top - offset.top) * (1 - delta));
        return { top };
    }
    calcOffset(command) {
        // TODO: add more alignment options as needed
        switch (command.align) {
            case SeFeScrollToAlignment.Center: return this.centerOffset(command);
            case SeFeScrollToAlignment.Top: return this.topOffset(command);
        }
    }
    calcBuffer(command) {
        const height = this.height(command.container);
        const vert = height - this.MIN_VISIBLE_CONTENT - this.commandOffsets(command).top;
        return { vert };
    }
    initialOffset(command) {
        const position = this.positionInContainer(command);
        const scrollTop = this.scrollTop(command.container);
        const top = position.top - scrollTop;
        return { top };
    }
    commandOffsets(command) {
        return { top: command.options.offset || this.DEFAULT_OFFSET };
    }
    topOffset(command) {
        return this.commandOffsets(command);
    }
    centerOffset(command) {
        const containerHeight = this.height(command.container);
        const targetHeight = command.target.offsetHeight;
        const offset = this.commandOffsets(command).top;
        const top = Math.max((containerHeight - targetHeight) / 2, offset);
        return { top };
    }
    positionInContainer(command) {
        const target = this.rect(command.target);
        const container = this.rect(command.container);
        const top = target.y - container.y + this.scrollTop(command.container);
        return { top };
    }
    easeOutBack(x) {
        return 1 + 2.5 * Math.pow(Math.pow(x, 2) - 1, 3) + 1.5 * Math.pow(Math.pow(x, 2) - 1, 2);
    }
    height(el) {
        return this.isWindow(el) ? el.innerHeight : el.clientHeight;
    }
    scrollTop(el) {
        return this.isWindow(el) ? el.scrollY : el.scrollTop;
    }
    containerEl(el) {
        return this.isWindow(el) ? this.document.body : el;
    }
    rect(el) {
        if (this.isWindow(el)) {
            const height = el.innerHeight;
            const width = el.innerWidth;
            return { left: 0, top: 0, x: 0, y: 0, right: width, width, bottom: height, height };
        }
        else {
            return el.getBoundingClientRect();
        }
    }
    isWindow(container) {
        return container === this.window;
    }
    updateFocus(target) {
        target === null || target === void 0 ? void 0 : target.setAttribute('tabindex', '-1');
        target === null || target === void 0 ? void 0 : target.focus();
    }
}
SeFeScrollService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeScrollService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SeFeScrollService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeScrollService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeScrollService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class SeFeScrollToDirective {
    constructor(element, seFeScrollService) {
        this.element = element;
        this.seFeScrollService = seFeScrollService;
        // noop
    }
    ngOnChanges(changes) {
        var _a;
        if ((_a = changes.seFeScrollTo) === null || _a === void 0 ? void 0 : _a.currentValue) {
            this.seFeScrollTo = false;
            this.seFeScrollService.scrollTo(this.alignment, this.element.nativeElement, this.options);
        }
    }
    get alignment() {
        switch (this.seFeScrollAlign) {
            case 'center': return SeFeScrollToAlignment.Center;
            default: return SeFeScrollToAlignment.Top;
        }
    }
    get options() {
        return {
            allowOverflow: this.seFeScrollOverflow,
            offset: this.seFeScrollOffset,
            duration: this.seFeScrollDuration,
            waitForRender: false,
        };
    }
}
SeFeScrollToDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeScrollToDirective, deps: [{ token: i0.ElementRef }, { token: SeFeScrollService }], target: i0.ɵɵFactoryTarget.Directive });
SeFeScrollToDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeScrollToDirective, selector: "[seFeScrollTo]", inputs: { seFeScrollTo: "seFeScrollTo", seFeScrollAlign: "seFeScrollAlign", seFeScrollDuration: "seFeScrollDuration", seFeScrollOffset: "seFeScrollOffset", seFeScrollOverflow: "seFeScrollOverflow" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeScrollToDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeScrollTo]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: SeFeScrollService }]; }, propDecorators: { seFeScrollTo: [{
                type: Input
            }], seFeScrollAlign: [{
                type: Input
            }], seFeScrollDuration: [{
                type: Input
            }], seFeScrollOffset: [{
                type: Input
            }], seFeScrollOverflow: [{
                type: Input
            }] } });

class SeFeScrollModule {
}
SeFeScrollModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeScrollModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeScrollModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeScrollModule, declarations: [SeFeScrollToDirective], imports: [CommonModule], exports: [SeFeScrollToDirective] });
SeFeScrollModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeScrollModule, providers: [
        SeFeScrollService
    ], imports: [[
            CommonModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeScrollModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        SeFeScrollToDirective
                    ],
                    exports: [
                        SeFeScrollToDirective
                    ],
                    providers: [
                        SeFeScrollService
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SeFeScrollModule, SeFeScrollService, SeFeScrollToAlignment, SeFeScrollToDirective };
